import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { JOURNAL_ENTRIES } from "@/lib/content";

/**
 * ArchiveStrip — a compact 3-tile "From the Archive" strip surfacing
 * evergreen essays from three distinct disciplines: Trusts, Bankruptcy,
 * and Business Succession. Placed below FeaturedPullquote so a first-time
 * visitor immediately sees the firm covers more than one discipline.
 *
 * Slugs are pinned so the same three canonical essays appear here;
 * editorial rotation happens via /journal, not the home page.
 */
const ARCHIVE_SLUGS = [
  "dynasty-trusts-2026-sunset",
  "chapter-7-chapter-11-two-reliefs",
  "buy-sell-agreements",
];

const DISCIPLINE_LABELS = {
  "dynasty-trusts-2026-sunset": "Trusts & Estate Planning",
  "chapter-7-chapter-11-two-reliefs": "Bankruptcy & Restructuring",
  "buy-sell-agreements": "Business Succession",
};

export default function ArchiveStrip() {
  const entries = ARCHIVE_SLUGS.map((slug) =>
    JOURNAL_ENTRIES.find((e) => e.slug === slug)
  ).filter(Boolean);

  if (entries.length === 0) return null;

  return (
    <section
      className="bg-ivory grain py-16 md:py-24"
      data-testid="archive-strip-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section header — restrained hairline treatment */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
          <div>
            <div className="flex items-baseline gap-3">
              <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                From the Archive
              </div>
              <Star size={9} className="text-brass fill-brass" />
            </div>
            <h3 className="mt-5 font-display text-3xl md:text-4xl text-navy leading-[1.05]">
              Three essays.{" "}
              <span className="italic font-cormorant text-oxblood">
                Three disciplines.
              </span>
            </h3>
          </div>
          <Link
            to="/journal"
            className="text-[10px] uppercase tracking-[0.32em] text-oxblood hover:text-navy transition-colors font-medium border-b border-brass/60 hover:border-navy pb-1 self-start md:self-end"
            data-testid="archive-strip-all-link"
          >
            Browse the Full Journal →
          </Link>
        </div>

        {/* Three tiles — hairline dividers, no boxy borders */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10"
          data-testid="archive-strip-tiles"
        >
          {entries.map((entry, i) => (
            <article
              key={entry.slug}
              className="hairline-top pt-6"
              data-testid={`archive-tile-${entry.slug}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium">
                  No. {String(i + 1).padStart(2, "0")}
                </span>
                <Star size={7} className="text-brass fill-brass" />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
                {DISCIPLINE_LABELS[entry.slug]}
              </div>
              <h4 className="mt-4 font-display text-navy text-xl md:text-2xl leading-[1.12]">
                <Link
                  to={`/journal/${entry.slug}`}
                  className="hover:text-oxblood transition-colors"
                  data-testid={`archive-tile-title-${entry.slug}`}
                >
                  {entry.title}
                </Link>
              </h4>
              <p className="mt-4 font-cormorant italic text-slate2 text-[15px] leading-snug line-clamp-3">
                {entry.excerpt}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">
                  {entry.minutes} min read
                </span>
                <span className="w-4 h-px bg-brass/50" />
                <Link
                  to={`/journal/${entry.slug}`}
                  className="text-[10px] uppercase tracking-[0.28em] text-oxblood hover:text-navy transition-colors font-medium"
                  data-testid={`archive-tile-cta-${entry.slug}`}
                >
                  Read the Essay →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
